/**
 * Canonical set of metrics we extract from every Lighthouse run.
 * Schema lives here once; ingest, storage, and API responses all reference it.
 */
import { z } from 'zod';

export const SummaryMetricsSchema = z.object({
  // ─── Web Vitals + timing ─────────────────────────────────────────────
  /** Largest Contentful Paint (ms) */
  lcp: z.number().nullable(),
  /** Cumulative Layout Shift (unitless) */
  cls: z.number().nullable(),
  /** Interaction to Next Paint (ms) */
  inp: z.number().nullable(),
  /** Time To First Byte (ms) */
  ttfb: z.number().nullable(),
  /** First Contentful Paint (ms) */
  fcp: z.number().nullable(),
  /** Speed Index (ms) */
  si: z.number().nullable(),
  /** Total Blocking Time (ms) */
  tbt: z.number().nullable(),

  // ─── Lighthouse category scores (0..1, null if category not run) ─────
  performance_score: z.number().min(0).max(1).nullable(),
  accessibility_score: z.number().min(0).max(1).nullable(),
  best_practices_score: z.number().min(0).max(1).nullable(),
  seo_score: z.number().min(0).max(1).nullable(),

  // ─── Page weight breakdown (bytes, transferSize) ─────────────────────
  // Sourced from `total-byte-weight` (total) + walking
  // `network-requests.details.items` by mimeType for the per-resource
  // splits. `.optional().nullable()` because:
  //   * older runner pods (pre-extension) send payloads without these
  //     keys — required-but-nullable would reject those callbacks with
  //     422 during a runner rollout (caught in webhooks integration
  //     tests after the initial #41 ship);
  //   * errored audits leave them null even with the new runner.
  // The dashboard hides bytes tiles when missing OR null.
  total_bytes: z.number().nullable().optional(),
  html_bytes: z.number().nullable().optional(),
  js_bytes: z.number().nullable().optional(),
  css_bytes: z.number().nullable().optional(),
  image_bytes: z.number().nullable().optional(),
  font_bytes: z.number().nullable().optional(),

  // ─── Third parties (aggregate across entities) ───────────────────────
  // From `third-party-summary` (LH ≤12) or `third-parties-insight`
  // (LH 13+). `third_party_bytes` is sum of entity `transferSize`;
  // `third_party_entity_count` is `details.items.length`.
  // `third_party_blocking_ms` is sum of `blockingTime` — LH 13's insight
  // no longer reports blocking time, so this is null on LH 13 runs
  // (the dashboard hides null tiles). Per-entity breakdown lives in the
  // raw LHR (the dashboard top-N table reads it there).
  third_party_bytes: z.number().nullable().optional(),
  third_party_blocking_ms: z.number().nullable().optional(),
  third_party_entity_count: z.number().int().nullable().optional(),

  // ─── Counts ──────────────────────────────────────────────────────────
  /** Number of network requests captured by the audit. */
  request_count: z.number().int().nullable().optional(),
  /** Total DOM node count (`dom-size` on LH ≤12, `dom-size-insight` on 13+). */
  dom_node_count: z.number().int().nullable().optional(),
  /** Max DOM depth (`dom-size` stat row / `dom-size-insight` debugData). */
  dom_max_depth: z.number().int().nullable().optional(),

  // ─── Opportunities (bytes savable) ───────────────────────────────────
  // `render_blocking_bytes` is the summed transfer size of the
  // render-blocking resources themselves — neither LH 12 nor LH 13 emits
  // an overall-savings-bytes figure for render blocking (only savings ms).
  render_blocking_bytes: z.number().nullable().optional(),
  render_blocking_count: z.number().int().nullable().optional(),
  unused_js_bytes: z.number().nullable().optional(),
  unused_css_bytes: z.number().nullable().optional(),

  // ─── Execution (ms) ──────────────────────────────────────────────────
  /** JS bootup time — `bootup-time.numericValue`. */
  bootup_time_ms: z.number().nullable().optional(),
  /** Total main-thread work — `mainthread-work-breakdown.numericValue`. */
  mainthread_total_ms: z.number().nullable().optional(),
});

export type SummaryMetrics = z.infer<typeof SummaryMetricsSchema>;

/** Audit keys we look up in the Lighthouse LHR. */
export const AUDIT_KEYS = {
  lcp: 'largest-contentful-paint',
  cls: 'cumulative-layout-shift',
  /**
   * Interaction to Next Paint — field-only metric.
   *
   * Lighthouse cannot compute INP in synthetic/lab mode: INP measures
   * latency from a real user interaction (click, tap, key) to the next
   * paint, and there are no interactions during a headless audit. The
   * LHR audit exists for shape parity but the value resolves to null
   * on every Clickweave synthetic run. INP populates once the RUM
   * pixel ingestion path lands and fills the field from real visitor
   * data (`plans/1.0.0/pixel-ingest/`).
   *
   * Dashboard surfaces hide the INP tile / row when every audit in
   * scope is null — see the page + site `TileGrid` in
   * `apps/dashboard-v2/src/routes/_app/origins/` and the audit-detail
   * `Overview` in `apps/dashboard-v2/src/routes/_app/settings/logs/`.
   */
  inp: 'interaction-to-next-paint',
  ttfb: 'server-response-time',
  fcp: 'first-contentful-paint',
  si: 'speed-index',
  tbt: 'total-blocking-time',

  // ─── New audits (page weight, third parties, opportunities, exec) ────
  // Whole-page weight + diagnostic counts.
  totalByteWeight: 'total-byte-weight',
  networkRequests: 'network-requests',
  // LH 13 removed `dom-size`; `dom-size-insight` is its `replacesAudits`
  // successor (numericValue is still total elements; max depth moves to
  // the stable `details.debugData.maxDepth`). Extraction reads whichever
  // is present.
  domSize: 'dom-size',
  domSizeInsight: 'dom-size-insight',
  // Aggregated third-party breakdown by entity (Google, Facebook, ...).
  // LH 13 removed `third-party-summary`; `third-parties-insight` is its
  // `replacesAudits` successor (entity is a plain string, no blockingTime).
  // Extraction reads whichever is present.
  thirdPartySummary: 'third-party-summary',
  thirdPartiesInsight: 'third-parties-insight',
  // Opportunities (the dashboard's existing Opportunities component
  // walks `categories.performance.auditRefs[]` for the full list; we
  // pin these three on the summary because they're the ones users
  // most often quote).
  // LH 13 removed `render-blocking-resources`; `render-blocking-insight`
  // replaces it (same one-row-per-resource items; no overallSavings*).
  renderBlockingResources: 'render-blocking-resources',
  renderBlockingInsight: 'render-blocking-insight',
  unusedJavascript: 'unused-javascript',
  unusedCssRules: 'unused-css-rules',
  // JS execution / main-thread.
  bootupTime: 'bootup-time',
  mainthreadWorkBreakdown: 'mainthread-work-breakdown',
} as const;

export const CATEGORY_KEYS = {
  performance_score: 'performance',
  accessibility_score: 'accessibility',
  best_practices_score: 'best-practices',
  seo_score: 'seo',
} as const;
